import React, { useCallback, useEffect, useMemo, useRef } from 'react';
import AutoSizer from 'react-virtualized-auto-sizer';
import { VariableSizeList as List } from 'react-window';
import { useSelectedAnnotationIds } from '../hooks/use-selected-annotation-ids';
import { useSidePanel } from '../side-panel-provider';
import { useAnnotationItemContainerActions } from './hooks/use-annotation-item-container-actions';
import { ANNOTATION_ITEM_HEIGHT, HEADER_HEIGHT } from './constants';
import type { AnnotationLabeledListProps, VirtualListDataT, VirtualListItem } from './types';
import { getLabelIcon, getLabelMappedAnnotations } from './utils';
import { getAnnotationItemKey, getHeaderItemKey } from './utils';
import { VirtualListItemRenderer } from './virtual-list-item';
const getItemKey = (index: number, data: VirtualListDataT) => {
  const item = data.items[index];
  if (!item) return `item-${index}`;
  return item.type === 'header' ? getHeaderItemKey(item.label) : getAnnotationItemKey(item.annotation.id);
};
function AnnotationLabeledListMemoized({
  annotations,
  importedAnnotationIds,
  header,
  item
}: AnnotationLabeledListProps) {
  const {
    toolConfig
  } = useSidePanel();
  const selectedAnnotationIds = useSelectedAnnotationIds();
  const listRef = useRef<List<VirtualListDataT>>(null);
  const sizeMap = useRef<Record<string, number>>({});
  const labelMappedAnnotations = getLabelMappedAnnotations(annotations, importedAnnotationIds);
  const {
    onMouseEnter,
    onMouseLeave,
    onClick,
    onKeyDown
  } = useAnnotationItemContainerActions({
    annotations
  });
  const virtualListItems = useMemo(() => {
    const items: VirtualListItem[] = [];
    Object.entries(labelMappedAnnotations).forEach(([label, labelDetails]) => {
      const headerIcon = getLabelIcon(labelDetails.headerInfo.iconConfig, toolConfig);
      items.push({
        type: 'header',
        label,
        headerInfo: labelDetails.headerInfo,
        icon: headerIcon
      });
      labelDetails.annotations.forEach(annotation => {
        const iconKey = labelDetails.headerInfo.iconConfig?.iconKey || annotation.annotationType || '';
        const annotationIcon = getLabelIcon({
          color: labelDetails.headerInfo.iconConfig?.color ?? '',
          iconKey
        }, toolConfig);
        items.push({
          type: 'annotation',
          annotation,
          icon: annotationIcon
        });
      });
    });
    return items;
  }, [labelMappedAnnotations, toolConfig]);
  const setSize = useCallback((itemKey: string, size: number) => {
    sizeMap.current = {
      ...sizeMap.current,
      [itemKey]: size
    };
    if (listRef.current) {
      const itemIndex = virtualListItems.findIndex(item => item.type === 'annotation' && getAnnotationItemKey(item.annotation.id) === itemKey);
      if (itemIndex !== -1) {
        listRef.current.resetAfterIndex(itemIndex);
      }
    }
  }, [virtualListItems]);
  const getItemSize = (index: number) => {
    const item = virtualListItems[index];
    if (!item) return ANNOTATION_ITEM_HEIGHT;
    if (item.type === 'header') {
      return HEADER_HEIGHT;
    }
    const itemKey = getAnnotationItemKey(item.annotation.id);
    return sizeMap.current[itemKey] || ANNOTATION_ITEM_HEIGHT;
  };
  useEffect(() => {
    if (selectedAnnotationIds.length > 0 && listRef.current) {
      const firstSelectedId = selectedAnnotationIds[0];
      const itemIndex = virtualListItems.findIndex(item => item.type === 'annotation' && item.annotation.id === firstSelectedId);
      if (itemIndex !== -1) {
        listRef.current?.scrollToItem(itemIndex, 'smart');
      }
    }
  }, [selectedAnnotationIds, virtualListItems]);
  const listData: VirtualListDataT = useMemo(() => ({
    items: virtualListItems,
    onMouseEnter,
    onMouseLeave,
    onClick,
    onKeyDown,
    headerComponent: header,
    itemComponent: item,
    setSize
  }), [virtualListItems, onMouseEnter, onMouseLeave, onClick, onKeyDown, header, item, setSize]);
  return <AutoSizer>
      {({
      width,
      height
    }) => <List<VirtualListDataT> ref={listRef} height={height} itemCount={virtualListItems.length} itemSize={getItemSize} width={width} itemData={listData} itemKey={getItemKey}>
          {VirtualListItemRenderer}
        </List>}
    </AutoSizer>;
}
export const AnnotationLabeledList = React.memo(AnnotationLabeledListMemoized);